import React, { useState, useEffect } from 'react';
import { apiCall, getUser } from '../lib/api.js';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full access to all features and settings' },
  { value: 'qa_scorer', label: 'QA Scorer', desc: 'Can score evaluations and view dashboard' },
  { value: 'agent', label: 'Agent', desc: 'Sees only their own evaluations, stats and appeals' },
  { value: 'ai_scorer', label: 'Service Scorer', desc: 'Automated scoring account. Its evaluations are visible to admins only and it never sends emails' },
];

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('qa_scorer');
  const [error, setError] = useState('');
  const currentUser = getUser();

  const fetchUsers = async () => {
    try { const d = await apiCall('/users'); setUsers(d.users || []); } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { fetchUsers(); }, []);

  const handleSave = async () => {
    if (!email.trim() || !email.includes('@')) { setError('Valid email required'); return; }
    setError('');
    try {
      await apiCall('/user/save', { method: 'POST', body: { email: email.trim().toLowerCase(), role } });
      setEmail(''); setRole('qa_scorer'); setShowForm(false);
      fetchUsers();
    } catch (e) { setError(e.message); }
  };

  const handleDelete = async (userEmail) => {
    if (userEmail === currentUser?.email) { alert("You can't remove yourself"); return; }
    if (!confirm('Remove ' + userEmail + '? They will lose access to the app.')) return;
    try { await apiCall('/user/delete', { method: 'POST', body: { email: userEmail } }); fetchUsers(); }
    catch (e) { alert(e.message); }
  };

  const handleRoleChange = async (userEmail, newRole) => {
    if (userEmail === currentUser?.email) { alert("You can't change your own role"); return; }
    try { await apiCall('/user/save', { method: 'POST', body: { email: userEmail, role: newRole } }); fetchUsers(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Control who can access the QA Scorecard app and their permission level.</p>
        <button onClick={() => setShowForm(s => !s)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs font-medium">
          {showForm ? 'Cancel' : '+ Add user'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          {error && <div className="bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Email address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="user@sabrent.com"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Role</label>
              <select value={role} onChange={e => setRole(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <button onClick={handleSave}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs font-medium">Add user</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {users.map(u => (
            <div key={u.email} className="px-5 py-3 flex items-center gap-4">
              <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-medium flex-shrink-0">
                {u.email.split('@')[0].slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{u.email}</div>
                {u.email === currentUser?.email && <span className="text-[11px] text-gray-400">You</span>}
              </div>
              <select value={u.role} onChange={e => handleRoleChange(u.email, e.target.value)}
                disabled={u.email === currentUser?.email}
                className={'px-2.5 py-1 rounded-lg text-xs font-medium border outline-none '
                  + (u.role === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-gray-50 text-gray-600 border-gray-200')
                  + (u.email === currentUser?.email ? ' opacity-50 cursor-not-allowed' : '')}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              {u.email !== currentUser?.email && (
                <button onClick={() => handleDelete(u.email)} className="text-gray-400 hover:text-red-500 transition" title="Remove">
                  <i className="ti ti-trash text-base" aria-hidden="true"></i>
                </button>
              )}
            </div>
          ))}
          {users.length === 0 && <div className="p-6 text-sm text-gray-400 text-center">No users configured</div>}
        </div>
      )}
    </div>
  );
}

function CompaniesTab() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const fetchCompanies = async () => {
    try { const d = await apiCall('/companies'); setCompanies(d.companies || []); } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { fetchCompanies(); }, []);

  const handleSave = async (data) => {
    try {
      await apiCall('/company/save', { method: 'POST', body: data });
      setEditing(null);
      fetchCompanies();
    } catch (e) { setError(e.message); }
  };

  const handleDelete = async (slug) => {
    if (!confirm('Remove this company? Evaluations will not be deleted.')) return;
    try { await apiCall('/company/delete', { method: 'POST', body: { slug } }); fetchCompanies(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Company branding appears on scorecard emails and the dashboard.</p>
        <button onClick={() => setEditing({})}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs font-medium">+ Add company</button>
      </div>

      {error && <div className="bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">{error}</div>}

      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {companies.map(c => (
            <div key={c.slug || c.id} className="px-5 py-3 flex items-center gap-4">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: c.brand_color || '#7c3aed' }}>
                {c.logo_url ? (
                  <img src={c.logo_url} alt={c.display_name} style={{ maxHeight: 36, maxWidth: 48 }} />
                ) : (
                  <span className="text-white text-sm font-bold tracking-wide">
                    {(c.display_name || c.name || '').split(/\s+/).map(w => w[0]).filter(Boolean).slice(0,2).join('').toUpperCase() || '?'}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium">{c.display_name || c.name}</h4>
                <p className="text-[11px] text-gray-400 font-mono">{c.slug}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-mono text-gray-400">{c.brand_color}</span>
                {c.disabled && <span className="text-[11px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded">Disabled</span>}
                <button onClick={() => setEditing(c)} className="text-gray-400 hover:text-purple-600 transition" title="Edit">
                  <i className="ti ti-pencil text-base" aria-hidden="true"></i>
                </button>
                <button onClick={() => handleDelete(c.slug)} className="text-gray-400 hover:text-red-500 transition" title="Remove">
                  <i className="ti ti-trash text-base" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          ))}
          {companies.length === 0 && <div className="p-6 text-sm text-gray-400 text-center italic">No companies yet. Add one to get started.</div>}
        </div>
      )}

      {/* Edit / Add Modal */}
      {editing && (
        <CompanyModal company={editing} onClose={() => setEditing(null)} onSave={handleSave} />
      )}
    </div>
  );
}

function CompanyModal({ company, onClose, onSave }) {
  const isNew = !company.slug;
  const [name, setName] = useState(company.display_name || company.name || '');
  const [brandColor, setBrandColor] = useState(company.brand_color || '#7c3aed');
  const [logoUrl, setLogoUrl] = useState(company.logo_url || '');
  const [disabled, setDisabled] = useState(!!company.disabled);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onSave({
      slug: company.slug || undefined,
      name: name.trim(),
      display_name: name.trim(),
      brand_color: brandColor,
      logo_url: logoUrl,
      disabled,
      created_at: company.created_at,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-medium mb-4">{isNew ? 'Add company' : 'Edit ' + (company.display_name || company.name)}</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Company name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Sabrent" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Brand color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={brandColor} onChange={e => setBrandColor(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border-0" />
                <input type="text" value={brandColor} onChange={e => setBrandColor(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Preview</label>
              <div className="h-10 rounded-lg flex items-center justify-center" style={{ background: brandColor }}>
                {logoUrl ? (
                  <img src={logoUrl} alt="Preview" style={{ maxHeight: 28 }} onError={e => e.target.style.display = 'none'} />
                ) : (
                  <span className="text-white text-xs font-bold tracking-wider">
                    {name.split(/\s+/).map(w => w[0]).filter(Boolean).slice(0,2).join('').toUpperCase() || '?'}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Logo URL (white/transparent PNG recommended)</label>
            <input type="text" value={logoUrl} onChange={e => setLogoUrl(e.target.value)}
              placeholder="https://..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500" />
          </div>

          {!isNew && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={disabled} onChange={e => setDisabled(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-red-500" />
              <span className="text-sm text-gray-600">Disable this company</span>
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium">Cancel</button>
          <button onClick={handleSubmit} disabled={saving || !name.trim()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 text-sm font-medium">
            {saving ? 'Saving...' : isNew ? 'Add company' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const user = getUser();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('users');

  const tabs = [
    { key: 'users', label: 'Users', icon: 'ti-users' },
    ...(isAdmin ? [{ key: 'companies', label: 'Companies', icon: 'ti-building' }] : []),
  ];

  return (
    <div>
      <h2 className="text-lg font-medium mb-5">Settings</h2>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 '
              + (activeTab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
            <i className={'ti ' + t.icon + ' text-sm'} aria-hidden="true"></i>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'companies' && isAdmin && <CompaniesTab />}
    </div>
  );
}
